import { useEffect, useState } from 'react'
import { useGooglePlacesAutocomplete, type PlaceResult } from '@/hooks/useGooglePlacesAutocomplete'
import { Loader2 } from 'lucide-react'
import { cn } from '@lib/utils/helpers'

interface AddressAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onPlaceSelect?: (place: PlaceResult) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  label?: string
  required?: boolean
  error?: string
  countryRestriction?: string | string[]
}

/**
 * Address input with Google Places Autocomplete
 */
export function AddressAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  placeholder = 'Enter address',
  disabled = false,
  className = '',
  label,
  required = false,
  error,
  countryRestriction,
}: AddressAutocompleteProps) {
  const [localValue, setLocalValue] = useState(value)
  const [validationMessage, setValidationMessage] = useState<string | null>(null)

  const { inputRef, isLoading, isReady, handleFocus } = useGooglePlacesAutocomplete({
    onPlaceSelect: (place) => {
      // Update input value
      const address = place.formatted_address || ''
      setLocalValue(address)
      onChange(address)

      // Show validation message if partial match
      if (place.partial_match) {
        setValidationMessage(
          'Warning: Address may be incomplete or ambiguous'
        )
      } else {
        setValidationMessage(null)
      }

      // Call custom handler
      if (onPlaceSelect) {
        onPlaceSelect(place)
      }
    },
    countryRestriction,
    enabled: !disabled,
  })

  // Ensure autocomplete is initialized on focus/click
  const handleInputFocus = () => {
    // Always call handleFocus to ensure initialization
    handleFocus()
  }

  const handleInputClick = () => {
    // Also handle click to ensure autocomplete is ready
    handleFocus()
  }

  // Sync external value changes
  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setLocalValue(newValue)
    onChange(newValue)
    setValidationMessage(null)
  }

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          className={cn(
            'w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-10',
            disabled && 'bg-gray-100 text-gray-500 cursor-not-allowed',
            error && 'border-red-500 focus:ring-red-500',
            isLoading && !disabled && 'pr-10'
          )}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        )}
        {isReady && !isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      {validationMessage && !error && (
        <p className="mt-1 text-sm text-amber-600">{validationMessage}</p>
      )}
    </div>
  )
}

